/**
 * PromptCraft 提示词扩展页面
 * 专业的 Grok 图像提示词工程师，专精 Flux.1 和 Aurora 模型
 */

import { Link } from 'react-router-dom'
import { Home, Shield, History, Sparkles, X } from 'lucide-react'
import { usePromptCraftState } from '../hooks/usePromptCraftState'
import { MindMapView } from '../components/promptcraft/MindMapView'
import { PromptCraftSidebar } from '../components/promptcraft/PromptCraftSidebar'
import { PromptCraftResult } from '../components/promptcraft/PromptCraftResult'
import { PromptCraftHistoryModal } from '../components/promptcraft/PromptCraftHistoryModal'

export function PromptCraftPage() {
  const state = usePromptCraftState()

  return (
    <div className="h-screen flex flex-col" style={{ background: '#0d1117' }}>
      {/* Header */}
      <header className="border-b border-cyan-900/30 bg-[#161b22] flex-shrink-0">
        <div className="px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Sparkles className="w-6 h-6 text-cyan-400" />
            <h1 className="text-xl font-bold text-white">PromptCraft</h1>
            <button
              onClick={state.handleNewTask}
              className="px-3 py-1.5 text-xs bg-cyan-900/20 text-cyan-400 rounded-lg hover:bg-cyan-900/40 transition-colors border border-cyan-900/50"
            >
              + 新任务
            </button>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => { state.setShowHistory(true); state.loadHistory() }} className="p-2 text-gray-400 hover:text-cyan-400 transition-colors" title="历史记录">
              <History className="w-5 h-5" />
            </button>
            <Link to="/" className="p-2 text-gray-400 hover:text-cyan-400 transition-colors" title="返回首页">
              <Home className="w-5 h-5" />
            </Link>
            <Link to="/admin" className="p-2 text-gray-400 hover:text-cyan-400 transition-colors" title="管理后台">
              <Shield className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </header>

      <div className="flex-1 flex gap-6 px-6 py-4 overflow-hidden">
        {/* Left Panel - Input */}
        <PromptCraftSidebar
          mode={state.mode}
          setMode={state.setMode}
          seedPrompt={state.seedPrompt}
          setSeedPrompt={state.setSeedPrompt}
          stylePreference={state.stylePreference}
          setStylePreference={state.setStylePreference}
          aspectRatio={state.aspectRatio}
          setAspectRatio={state.setAspectRatio}
          showAdvanced={state.showAdvanced}
          setShowAdvanced={state.setShowAdvanced}
          decomposePrompt={state.decomposePrompt}
          setDecomposePrompt={state.setDecomposePrompt}
          visionPreviewUrl={state.visionPreviewUrl}
          isDragging={state.isDragging}
          setIsDragging={state.setIsDragging}
          visionUnderstanding={state.visionUnderstanding}
          fileInputRef={state.fileInputRef}
          isGenerating={state.isGenerating}
          visionFile={state.visionFile}
          onGenerate={state.handleGenerate}
          onDecompose={state.handleDecompose}
          onVisionFileSelect={state.handleVisionFileSelect}
          onVisionAnalyze={state.handleVisionAnalyze}
          onClearVision={state.handleClearVision}
          onOpenPreview={state.handleOpenPreview}
        />

        {/* Right Panel - Results */}
        <div className="flex-1 flex flex-col gap-4 min-w-0 overflow-hidden">
          {/* Prompt Result */}
          {state.expandedResult && (
            <PromptCraftResult
              expandedResult={state.expandedResult}
              copied={state.copied}
              isRegenerating={state.isRegenerating}
              previewLoading={!!state.previewImage?.loading}
              onRegenerate={state.handleRegenerate}
              onCopy={state.handleCopy}
              onGeneratePreview={state.handleGeneratePreview}
            />
          )}

          {/* Mind Map + Edit Panel */}
          <div className="flex-1 flex gap-3 min-h-0">
            <MindMapView
              nodes={state.nodes}
              edges={state.edges}
              onNodesChange={state.onNodesChange}
              onEdgesChange={state.onEdgesChange}
              onNodeClick={state.onNodeClick}
              onNodeDragStart={state.onNodeDragStart}
              onNodeDrag={state.onNodeDrag}
              onNodeDragStop={state.onNodeDragStop}
            />

            {/* Edit Panel */}
            {state.editingNodeId && (
              <div className="w-72 flex-shrink-0 bg-[#161b22] rounded-xl border border-cyan-500/50 p-4 flex flex-col gap-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-cyan-400">
                    编辑字段：{
                      state.editingNodeId.startsWith('subject-value-')
                        ? state.editingNodeId.replace('subject-value-', '').replace(/_/g, ' ')
                        : state.editingNodeId.replace('-content', '').replace(/_/g, ' ')
                    }
                  </span>
                  <button onClick={state.handleCancelEdit} className="text-gray-500 hover:text-gray-300">
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <textarea
                  value={state.editingValue}
                  onChange={(e) => state.setEditingValue(e.target.value)}
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); state.handleSaveEdit() }
                    if (e.key === 'Escape') state.handleCancelEdit()
                  }}
                  className="flex-1 bg-[#0d1117] text-gray-200 border border-cyan-900/50 rounded-lg p-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500"
                  style={{ minHeight: '120px' }}
                />
                <div className="flex gap-2 justify-end">
                  <button onClick={state.handleCancelEdit} className="px-3 py-1.5 text-sm bg-[#0d1117] text-gray-400 rounded-lg hover:bg-gray-800 border border-gray-700">取消</button>
                  <button onClick={state.handleSaveEdit} className="px-3 py-1.5 text-sm bg-cyan-500 text-[#0d1117] rounded-lg hover:bg-cyan-400 font-medium">保存</button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* History Modal */}
      {state.showHistory && (
        <PromptCraftHistoryModal
          historyRecords={state.historyRecords}
          historyLoading={state.historyLoading}
          onClose={() => state.setShowHistory(false)}
          onRestore={state.restoreFromRecord}
          onRefresh={state.loadHistory}
        />
      )}

      {/* Preview Modal */}
      {state.previewModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50" onClick={() => state.setPreviewModal(null)}>
          <div className="relative max-w-4xl max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => state.setPreviewModal(null)} className="absolute -top-10 right-0 text-white/60 hover:text-white">
              <X className="w-6 h-6" />
            </button>
            <img src={state.previewModal.url} alt="Preview" className="max-w-full max-h-[80vh] rounded-lg shadow-2xl" />
            <p className="mt-3 text-sm text-gray-400 max-w-2xl">{state.previewModal.prompt}</p>
          </div>
        </div>
      )}
    </div>
  )
}
